import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import "../../../styles/css/custom-select.css";

interface Option {
  key: string;
  value: string;
}

interface CustomSelectProps {
  id: string;
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  isRequired?: boolean;
}

function CustomSelect({ id, label, value, options, onChange, isRequired }: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutsideClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, []);

  const selectedLabel = options.find((o) => o.key === value)?.value ?? "";
  const hasValue = !!value;

  return (
    <div className="custom-select-wrap" ref={wrapRef}>
      <button
        id={id}
        type="button"
        className={[
          "custom-select-btn",
          open ? "custom-select-btn--open" : "",
          !hasValue ? "custom-select-btn--empty" : "",
        ].join(" ")}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selectedLabel}
      </button>

      <FaChevronDown
        size={12}
        className={`custom-select-chevron ${open ? "custom-select-chevron--up" : ""}`}
      />

      <label
        htmlFor={id}
        className={`custom-select-label ${hasValue || open ? "custom-select-label--float" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        style={{ cursor: "pointer" }}
      >
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>

      {open && (
        <ul className="custom-select-menu" role="listbox">
          {options.map((option, i) => (
            <li
              key={i}
              role="option"
              aria-selected={option.key === value}
              className={[
                "custom-select-option",
                option.key === value ? "custom-select-option--selected" : "",
              ].join(" ")}
              onClick={() => {
                onChange(option.key);
                setOpen(false);
              }}
            >
              {option.value}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CustomSelect;
